import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';

import type { StagingData } from '@/shared/types/staging';
import { stagingService } from '@/shared/api/staging-service';
import { useModalStore } from '@/shared/stores/modal-store';
import { BaseModal, Button, CustomInput } from '@/shared/ui';

interface StagingsPage {
  data: {
    stagings: StagingData[];
  };
  pageInfo?: {
    hasMore: boolean;
    nextCursor?: string;
  };
}

export const EditStagingModal = () => {
  const { isModalOpen, closeModal, getModalData } = useModalStore();
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');

  const isOpen = isModalOpen('edit-staging');
  const modalData = getModalData('edit-staging');
  const staging = (modalData as { staging?: StagingData })?.staging;

  // Update name when staging data changes
  useEffect(() => {
    if (staging) {
      setName(staging.name || '');
    }
  }, [staging]);

  const handleClose = () => {
    closeModal('edit-staging');
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!staging) return;

    try {
      setIsLoading(true);
      setError(null);

      // Check if name has changed
      const hasChanges = name !== staging.name;

      if (!hasChanges) {
        handleClose();
        return;
      }

      if (!name.trim()) {
        setError('Name cannot be empty');
        return;
      }

      await stagingService.updateStaging(staging.id, { name: name.trim() });

      // Update React Query cache for the individual staging
      queryClient.setQueryData<StagingData | null>(['staging', staging.id], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          name: name.trim(),
        };
      });

      // Update React Query cache for the infinite stagings list
      queryClient.setQueryData<InfiniteData<StagingsPage>>(
        ['stagings', 'infinite'],
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: {
                ...page.data,
                stagings: page.data.stagings.map((s: StagingData) =>
                  s.id === staging.id ? { ...s, name: name.trim() } : s
                ),
              },
            })),
          };
        }
      );

      toast.success('Staging updated successfully');
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update staging');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !staging) return null;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Staging"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4 p-5">
        {/* Name input */}
        <div>
          <label className="block text-sm font-medium text-zinc-300 mb-2">
            Name
          </label>
          <CustomInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter staging name"
            disabled={isLoading}
            className="w-[300px]"
          />
        </div>

        {/* Error message */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3 justify-end pt-4">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isLoading || !name.trim()}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
    </BaseModal>
  );
};
